import React, { useEffect, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import TimelineModule from "highcharts/modules/timeline";
import { lifestyleApi } from "../apiService/api";
import { getUrlParams } from "../apiService/timespam";
import SectionHeader from "../Components/SectionHeader";
import { navigateWithFai, triggerShare } from "../utils/Utils";
import "./HealthTimeline.css";

if (typeof Highcharts === "object") {
  const initTimeline = TimelineModule.default || TimelineModule;
  if (typeof initTimeline === "function" && !Highcharts.__timelineInitialized) {
    initTimeline(Highcharts);
    Highcharts.__timelineInitialized = true;
  }
}

// Shimmer helpers (same pattern as other lifestyle cards)
const ensureShimmerKeyframes = () => {
  if (typeof document === "undefined") return;
  if (document.getElementById("shimmer-keyframes")) return;

  const style = document.createElement("style");
  style.id = "shimmer-keyframes";
  style.innerHTML = `
    @keyframes shimmer {
      0% { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
  `;
  document.head.appendChild(style);
};

const shimmerBaseStyle = {
  position: "relative",
  overflow: "hidden",
  backgroundImage:
    "linear-gradient(90deg, var(--shimmer-1) 0%, var(--shimmer-2) 50%, var(--shimmer-1) 100%)",
  backgroundSize: "200% 100%",
  animation: "shimmer 1.3s linear infinite",
};

const HealthTimelineSkeleton = () => {
  useEffect(() => {
    ensureShimmerKeyframes();
  }, []);

  const placeholderDots = [1, 2, 3, 4];

  return (
    <div className="health-timeline-root">
      <div className="health-timeline-card">
        {/* SectionHeader skeleton */}
        <div style={{ padding: "12px 16px 0 16px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            {/* Icon */}
            <div
              style={{
                ...shimmerBaseStyle,
                width: 32,
                height: 32,
                borderRadius: 999,
              }}
            />

            {/* Title + subtitle */}
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              <div
                style={{
                  ...shimmerBaseStyle,
                  height: 18,
                  width: "60%",
                  borderRadius: 8,
                }}
              />
              <div
                style={{
                  ...shimmerBaseStyle,
                  height: 14,
                  width: "40%",
                  borderRadius: 8,
                }}
              />
            </div>

            {/* Info icon */}
            <div
              style={{
                ...shimmerBaseStyle,
                width: 20,
                height: 20,
                borderRadius: 999,
              }}
            />
          </div>
        </div>

        {/* Timeline skeleton */}
        <div className="health-timeline-chart-wrapper">
          <div
            style={{
              ...shimmerBaseStyle,
              borderRadius: 16,
              height: 260,
              margin: "8px 0",
              padding: "12px 0",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-around",
            }}
          >
            {placeholderDots.map((i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                {/* Dot */}
                <div
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    backgroundColor: "var(--bg-surface)",
                  }}
                />
                {/* Label box */}
                <div
                  style={{
                    ...shimmerBaseStyle,
                    width: 110,
                    height: 40,
                    borderRadius: 6,
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const STATUS_COLORS = {
  elite: "#4B78C1",
  fit: "#58C063",
  active: "#F8C01B",
  sedentary: "#F76E66",
};
const PALETTE = Object.values(STATUS_COLORS);

const HealthTimeline = ({ shareConfig } = {}) => {
  const [timelineData, setTimelineData] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const { encryptedId } = getUrlParams();
    if (!encryptedId) return;

    lifestyleApi
      .healthtimelineData(encryptedId)
      .then((res) => {
        if (res?.data?.code === 200 && res.data.data) {
          setTimelineData(res.data.data);
        }
      })
      .catch((err) => {
        console.error("Error fetching health timeline", err);
      });
  }, []);

  if (!timelineData) return <HealthTimelineSkeleton />;

  const { theme } = getUrlParams();
  const isDark = theme === "dark" || document.documentElement.classList.contains("dark");

  const getPointColor = (item, index) => {
    if (item.color) return item.color;

    const label = (item.label || "").toLowerCase();
    if (label.includes("elite")) return STATUS_COLORS.elite;
    if (label.includes("fit")) return STATUS_COLORS.fit;
    if (label.includes("active")) return STATUS_COLORS.active;
    if (label.includes("sedentary")) return STATUS_COLORS.sedentary;

    return PALETTE[index % PALETTE.length];
  };

  const seriesData = (timelineData.journey || []).map((item, index) => ({
    name: item.name || "",
    label: item.label || "",
    description: item.description || "",
    color: getPointColor(item, index),
  }));

  const isSmallMobile = windowWidth < 420;
  const isMobile = windowWidth < 600;
  const isTablet = windowWidth < 900;

  const chartHeight = isSmallMobile ? 400 : isMobile ? 300 : isTablet ? 340 : 400;
  const connectorWidth = isSmallMobile ? 1 : 2;

  const options = {
    chart: {
      type: "timeline",
      inverted: true,
      backgroundColor: "transparent",
      height: chartHeight,
      marginTop: 20,
      marginBottom: 20,
      spacingTop: 0,
      spacingBottom: 0,
      animation: {
        duration: 1500,
      },
    },

    title: { text: null },
    subtitle: { text: null },
    credits: { enabled: false },

    tooltip: { enabled: false },

    xAxis: {
      visible: false,
      type: "category",
    },

    yAxis: {
      visible: false,
    },

    legend: { enabled: false },

    plotOptions: {
      series: {
        animation: {
          duration: 1500,
          easing: "easeOutQuad",
        },
        states: {
          hover: { enabled: false },
          inactive: { enabled: false },
        },
        enableMouseTracking: false,
        colorByPoint: true,
        clip: false,
      },
      timeline: {
        lineWidth: 4,
        lineColor: isDark ? "#3a4a40" : "#c4c4c4",
        connectorWidth,
        dataLabels: {
          enabled: true,
          alternate: true,
          distance: 40,
          backgroundColor: isDark ? "#1e2a22" : "#ffffff",
          borderRadius: 3,
          padding: 4,
          shadow: false,
          useHTML: true,
          crop: false,
          overflow: "allow",
          style: {
            fontFamily: "Open Sans, sans-serif",
            fontSize: isMobile ? "10px" : "11px",
            lineHeight: "14px",
          },
          formatter: function () {
            const p = this.point;
            const textColor = isDark ? "#e0e0e0" : "#333333";
            return `
              <div style="line-height:14px; color:${textColor};">
                <div style="font-weight:600;">
                  <span style="color:${p.color}">&#9679;</span>
                  &nbsp;${p.name || ""}
                </div>
                <div>${p.label || ""}</div>
              </div>
            `;
          },
        },
        marker: {
          enabled: false,
        },
      },
    },

    series: [
      {
        type: "timeline",
        data: seriesData,
      },
    ],
  };

  return (
    <div className="health-timeline-root">
      <div className="health-timeline-card">
        <SectionHeader
          icon="https://appcdn.goqii.com/storeimg/70822_1764052676.png"
          infoIconLight="https://appcdn.goqii.com/storeimg/38298_1764052060.png"
          infoIconDark="https://appcdn.goqii.com/storeimg/26151_1769593659.png"
          title={timelineData.title}
          subtitle={timelineData.heading}
          showInfo
          onInfoClick={() => {
            navigateWithFai("My Health Timeline", timelineData.infoUrl);
          }}
          showShare={!!shareConfig?.isSharable}
          onShareClick={() => triggerShare(shareConfig?.text)}
        />

        <div className="health-timeline-chart-wrapper">
          <HighchartsReact highcharts={Highcharts} options={options} immutable />
        </div>
      </div>
    </div>
  );
};

export default HealthTimeline;
